import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Skeleton } from "@/components/ui/skeleton";
import { Check, ChevronDown, RotateCcw, X } from "lucide-react";

const blockFilters = [
  { key: "all", label: "Tutti" },
  { key: "1", label: "Medicina" },
  { key: "2", label: "Regolamento" },
  { key: "3", label: "Carte Federali" },
  { key: "4", label: "Indirizzi tecnici" },
];

function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "short" });
  } catch {
    return "";
  }
}

export default function Ripasso() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [filter, setFilter] = useState("all");
  const [showResolved, setShowResolved] = useState(false);
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    api
      .get("/review/errors")
      .then((r) => setData(r.data))
      .catch(() => setError(true));
  }, []);

  const filtered = useMemo(() => {
    if (!data) return [];
    return data.items
      .filter((i) => (filter === "all" ? true : String(i.blocco) === filter))
      .filter((i) => (showResolved ? true : !i.lastWasCorrect));
  }, [data, filter, showResolved]);

  const activeInFilter = useMemo(() => filtered.filter((i) => !i.lastWasCorrect).length, [filtered]);

  if (error) {
    return (
      <div className="pt-10 text-center">
        <p className="font-bold">Impossibile caricare il ripasso.</p>
        <button
          onClick={() => window.location.reload()}
          className="press mt-3 rounded-full border border-[color:var(--hairline)] px-5 py-2.5 min-h-[44px] font-semibold text-sm hover:bg-[color:var(--bg-subtle)] transition-colors duration-200"
          data-testid="review-retry-button"
        >
          Riprova
        </button>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="pt-6 space-y-4">
        <Skeleton className="h-8 w-2/3" />
        <Skeleton className="h-10 w-full rounded-full" />
        <Skeleton className="h-64 w-full rounded-3xl" />
      </div>
    );
  }

  const { counts } = data;
  const hasErrors = counts.toReview > 0;

  return (
    <div className="pt-6">
      <h1 className="text-3xl font-extrabold tracking-tight fade-up d1">Ripasso errori.</h1>
      <p className="mt-2 text-sm text-[color:var(--text-2)] leading-6 fade-up d1">
        Le domande che sbagli finiscono qui. Rifalle finché non le padroneggi: escono dalla lista quando la tua ultima risposta è corretta.
      </p>

      {/* Summary + main CTA */}
      <section className="mt-5 rounded-3xl bg-[color:var(--ink)] text-white p-6 fade-up d2" data-testid="review-summary-card">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-white/50">Da ripassare</p>
            <p className="mt-1 font-mono text-4xl font-semibold tracking-tight" data-testid="review-toreview-count">
              {counts.toReview}
            </p>
            <p className="mt-1 font-mono text-[11px] text-white/50">
              {[1, 2, 3, 4].map((b) => `B${b}:${counts.perBlock[String(b)] || 0}`).join(" · ")}
              {counts.resolved > 0 && <span className="text-[color:var(--volt)]"> · {counts.resolved} risolte</span>}
            </p>
          </div>
          {hasErrors && (
            <button
              onClick={() => navigate(`/esame?mode=review${filter !== "all" ? `&blocco=${filter}` : ""}`)}
              data-testid="review-start-button"
              className="press inline-flex items-center gap-2 rounded-full bg-[color:var(--volt)] text-[color:var(--ink)] font-extrabold px-6 py-3 min-h-[44px] hover:brightness-95 transition-all duration-200"
            >
              <RotateCcw className="w-4 h-4" aria-hidden="true" />
              Rifai queste domande
            </button>
          )}
        </div>
        {hasErrors && (
          <p className="mt-3 text-xs text-white/50">
            Max 15 domande a sessione, le più sbagliate prima{filter !== "all" ? ` — solo ${blockFilters.find((f) => f.key === filter)?.label}` : ""}.
          </p>
        )}
      </section>

      {/* Empty state */}
      {!hasErrors && (
        <div className="mt-6 rounded-3xl border border-[color:var(--hairline)] bg-white p-8 text-center fade-up d3" data-testid="review-empty-state">
          <p className="font-extrabold text-lg">Zero errori da ripassare.</p>
          <p className="mt-2 text-sm text-[color:var(--text-2)] leading-6">
            {counts.resolved > 0
              ? "Hai recuperato tutti i tuoi errori. Continua ad allenarti per mantenerli a zero."
              : "Fai una simulazione: le domande sbagliate compariranno qui, pronte per il ripasso."}
          </p>
          <Link
            to="/simulazioni"
            data-testid="review-goto-simulations-button"
            className="press mt-5 inline-block rounded-full bg-[color:var(--ink)] text-white font-extrabold px-6 py-3 min-h-[44px] hover:bg-black/85 transition-colors duration-200"
          >
            Vai alle simulazioni
          </Link>
        </div>
      )}

      {hasErrors && (
        <>
          {/* Block filter */}
          <div className="mt-6 flex gap-2 overflow-x-auto pb-1 -mx-4 px-4 fade-up d3" data-testid="review-filter-tabs">
            {blockFilters.map((f) => (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                data-testid={`review-filter-${f.key}`}
                aria-pressed={filter === f.key}
                className={`press shrink-0 rounded-full px-4 py-2 min-h-[36px] text-xs font-bold font-mono uppercase tracking-wide border transition-colors duration-200 ${
                  filter === f.key
                    ? "bg-[color:var(--ink)] text-white border-[color:var(--ink)]"
                    : "bg-white text-[color:var(--ink)] border-[color:var(--hairline)] hover:bg-[color:var(--bg-subtle)]"
                }`}
              >
                {f.label}
                {f.key !== "all" && (
                  <span className={`ml-1.5 ${filter === f.key ? "text-[color:var(--volt)]" : "text-[color:var(--text-2)]"}`}>
                    {counts.perBlock[f.key] || 0}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* List header + resolved toggle */}
          <div className="mt-5 flex items-center justify-between">
            <h2 className="text-sm font-extrabold tracking-[0.14em] uppercase">
              Domande <span className="font-mono text-[color:var(--text-2)]">({filtered.length})</span>
            </h2>
            {counts.resolved > 0 && (
              <button
                onClick={() => setShowResolved(!showResolved)}
                data-testid="review-toggle-resolved-button"
                aria-pressed={showResolved}
                className="text-xs font-bold underline underline-offset-2"
              >
                {showResolved ? "Nascondi risolte" : "Mostra anche risolte"}
              </button>
            )}
          </div>

          {filtered.length === 0 && (
            <p className="mt-4 text-sm text-[color:var(--text-2)] py-8 text-center rounded-3xl border border-dashed border-[color:var(--hairline)]" data-testid="review-filter-empty">
              Nessun errore da ripassare per questo blocco. Ottimo lavoro.
            </p>
          )}

          <div className="mt-3 space-y-2.5">
            {filtered.map((item) => {
              const open = openId === item.questionId;
              const resolved = item.lastWasCorrect;
              return (
                <div key={item.questionId} className="rounded-2xl border border-[color:var(--hairline)] bg-white overflow-hidden">
                  <button
                    onClick={() => setOpenId(open ? null : item.questionId)}
                    data-testid="review-item"
                    aria-expanded={open}
                    className="w-full text-left px-4 py-3.5 flex items-start gap-3 hover:bg-[color:var(--bg-subtle)] transition-colors duration-200"
                  >
                    <span
                      className={`mt-0.5 shrink-0 w-5 h-5 rounded-full flex items-center justify-center ${
                        resolved ? "bg-[color:var(--volt)]" : "bg-[color:var(--danger)] text-white"
                      }`}
                      aria-hidden="true"
                    >
                      {resolved ? <Check className="w-3 h-3" strokeWidth={3} /> : <X className="w-3 h-3" strokeWidth={3} />}
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="block text-sm font-semibold leading-5">{item.testo}</span>
                      <span className="mt-1.5 flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-[10px] tracking-widest uppercase text-[color:var(--text-2)] border border-[color:var(--hairline)] rounded-full px-2 py-0.5">
                          B{item.blocco} · {item.bloccoNome}
                        </span>
                        <span
                          className={`text-[10px] font-extrabold uppercase tracking-wide rounded-full px-2 py-0.5 ${
                            resolved ? "bg-[color:var(--volt)]" : "bg-white border border-[color:var(--danger)]/40 text-[color:var(--danger)]"
                          }`}
                          data-testid="review-item-status"
                        >
                          {resolved ? "Risolta" : `Sbagliata ${item.wrongCount}×`}
                        </span>
                        {item.lastSeenAt && (
                          <span className="font-mono text-[10px] text-[color:var(--text-2)]">ultima: {fmtDate(item.lastSeenAt)}</span>
                        )}
                      </span>
                    </span>
                    <ChevronDown
                      className={`w-4 h-4 mt-1 text-[color:var(--text-2)] shrink-0 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
                      aria-hidden="true"
                    />
                  </button>
                  {open && (
                    <div className="px-4 pb-4 pt-1 border-t border-[color:var(--hairline)] fade-up" data-testid="review-item-detail">
                      <div className="space-y-1.5 mt-3">
                        {item.opzioni.map((opt, j) => {
                          const isCorrect = j === item.correttaIndex;
                          return (
                            <div
                              key={j}
                              className={`rounded-xl px-3 py-2.5 text-sm flex items-center justify-between gap-2 ${
                                isCorrect ? "bg-[color:var(--volt)] font-bold" : "bg-[color:var(--bg-subtle)] text-[color:var(--text-2)]"
                              }`}
                            >
                              <span>{opt}</span>
                              {isCorrect && <Check className="w-3.5 h-3.5 shrink-0" strokeWidth={3} aria-hidden="true" />}
                            </div>
                          );
                        })}
                      </div>
                      {item.spiegazione && (
                        <div className="mt-3 rounded-xl bg-[color:var(--bg-subtle)] p-3">
                          <p className="font-mono text-[10px] tracking-widest uppercase text-[color:var(--text-2)] mb-1">Spiegazione</p>
                          <p className="text-sm leading-6">{item.spiegazione}</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Bottom CTA when list is long */}
          {activeInFilter > 0 && filtered.length > 5 && (
            <button
              onClick={() => navigate(`/esame?mode=review${filter !== "all" ? `&blocco=${filter}` : ""}`)}
              data-testid="review-start-bottom-button"
              className="press mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[color:var(--ink)] text-white font-extrabold px-5 py-3.5 min-h-[44px] hover:bg-black/85 transition-colors duration-200"
            >
              <RotateCcw className="w-4 h-4" aria-hidden="true" /> Rifai queste domande
            </button>
          )}
        </>
      )}
    </div>
  );
}
